class ArticleEditorial extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.content = this.querySelector('[data-article-editorial-content]');
    this.readingTimes = this.querySelectorAll('[data-article-reading-time]');
    this.desktopToc = this.querySelector('[data-article-toc-desktop]');
    this.mobileToc = this.querySelector('[data-article-toc-mobile]');

    if (!this.content) return;

    this.removeRelatedProductPlaceholders();
    this.removeExtraBreaks();
    this.removeEmptyContent();
    this.initializeContentImages();
    this.updateReadingTime();
    this.createToc();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.headingObserver?.disconnect();
    cancelAnimationFrame(this.headingFrame);
    clearTimeout(this.headingScrollTimer);
  }

  removeRelatedProductPlaceholders() {
    const walker = document.createTreeWalker(this.content, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      if (node.parentElement?.closest('pre, code')) return;
      const nextText = node.textContent.replace(/\[\[related_products_\d+\]\]/g, '');
      if (nextText !== node.textContent) node.textContent = nextText;
    });
  }

  removeExtraBreaks() {
    const adjacentContent = (node, direction) => {
      let sibling = node[direction];
      while (sibling && (sibling.nodeType === Node.COMMENT_NODE ||
        (sibling.nodeType === Node.TEXT_NODE && !sibling.textContent.trim()))) {
        sibling = sibling[direction];
      }
      return sibling;
    };
    const isBlock = (node) => node?.nodeType === Node.ELEMENT_NODE &&
      /^(P|DIV|H[1-6]|UL|OL|LI|TABLE|BLOCKQUOTE|HR)$/.test(node.tagName);

    this.content.querySelectorAll('br').forEach((lineBreak) => {
      if (lineBreak.closest('pre, code')) return;
      const previous = adjacentContent(lineBreak, 'previousSibling');
      let next = adjacentContent(lineBreak, 'nextSibling');
      while (next?.nodeName === 'BR') next = adjacentContent(next, 'nextSibling');
      if (previous?.nodeName === 'BR' || isBlock(previous) || isBlock(next) ||
        ((!previous || !next) && lineBreak.parentElement.matches('p, div, h1, h2, h3, h4, h5, h6, li, th, td'))) {
        lineBreak.remove();
      }
    });
  }

  removeEmptyContent() {
    const isEmpty = (node) => {
      if (node.nodeType === Node.TEXT_NODE) return !node.textContent.trim();
      if (node.nodeType === Node.COMMENT_NODE) return true;
      if (node.nodeType !== Node.ELEMENT_NODE ||
        !node.matches('p, div, span, strong, b, em, i, u, s, small, font, h1, h2, h3, h4, h5, h6, blockquote, br')) return false;
      return [...node.childNodes].every(isEmpty);
    };
    // Keep media, embeds, anchor targets, and table cells even when they have no text.
    this.content.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, blockquote').forEach((element) => {
      if (element.closest('pre, code') || element.id || element.querySelector('[id], [name]')) return;
      if (isEmpty(element)) element.remove();
    });
  }

  initializeContentImages() {
    this.content.querySelectorAll('img').forEach((image) => {
      if (image.closest('blur-image, [data-blur-image]')) return;

      if (!image.hasAttribute('loading')) image.loading = 'lazy';

      const width = Number.parseInt(image.getAttribute('width'), 10);
      const height = Number.parseInt(image.getAttribute('height'), 10);
      const placeholderUrl = this.createPlaceholderUrl(image.currentSrc || image.src);
      if (!width || !height || !placeholderUrl) return;

      const wrapper = document.createElement('blur-image');
      wrapper.className = 'blur-image-wrapper article-editorial__content-image';
      wrapper.style.setProperty('--aspect-ratio', String(height / width));

      const placeholder = image.cloneNode(false);
      placeholder.removeAttribute('id');
      placeholder.removeAttribute('srcset');
      placeholder.removeAttribute('sizes');
      placeholder.removeAttribute('data-src');
      placeholder.removeAttribute('data-srcset');
      placeholder.src = placeholderUrl;
      placeholder.alt = '';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.className = 'blur-image__placeholder';

      image.classList.add('blur-image__main');
      const parent = image.parentNode;
      const nextSibling = image.nextSibling;
      wrapper.append(placeholder, image);
      parent.insertBefore(wrapper, nextSibling);
    });

    window.BlurImage?.bind(this.content);
  }

  createPlaceholderUrl(source) {
    if (!source) return null;

    try {
      const url = new URL(source, window.location.href);
      const isShopifyImage = url.hostname.endsWith('shopify.com') || url.pathname.includes('/cdn/shop/');
      if (!isShopifyImage) return null;
      url.searchParams.set('width', '20');
      return url.toString();
    } catch {
      return null;
    }
  }

  updateReadingTime() {
    const visibleText = this.content.textContent.replace(/\s/g, ' ');
    const minutes = Math.max(1, Math.ceil(visibleText.length / 1200));
    this.readingTimes.forEach((readingTime) => { readingTime.textContent = `${minutes} min read`; });
  }

  createToc() {
    const usedIds = new Set();
    this.tocItems = [...this.content.querySelectorAll('h2')].map((heading, index) => {
      const baseId = heading.id || `article-section-${index + 1}`;
      let id = baseId;
      let duplicate = 2;
      while (usedIds.has(id) || (document.getElementById(id) && document.getElementById(id) !== heading)) id = `${baseId}-${duplicate++}`;
      heading.id = id;
      usedIds.add(id);
      return { id, label: heading.textContent.trim(), element: heading };
    }).filter(({ label }) => label);

    if (!this.tocItems.length) {
      this.desktopToc?.setAttribute('hidden', '');
      this.mobileToc?.setAttribute('hidden', '');
      return;
    }

    this.querySelectorAll('[data-article-toc-list]').forEach((list) => this.renderTocItems(list));
    this.addEventListener('click', (event) => this.handleClick(event), { signal: this.abortController.signal });
    this.observeHeadings();
  }

  renderTocItems(list) {
    list.replaceChildren();
    this.tocItems.forEach(({ id, label }) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.setAttribute('href', `#${encodeURIComponent(id)}`);
      link.dataset.articleTocLink = id;
      link.textContent = label;
      item.appendChild(link);
      list.appendChild(item);
    });
  }

  handleClick(event) {
    const toggle = event.target.closest('[data-article-toc-toggle]');
    if (toggle) {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      this.mobileToc.classList.toggle('is-open', !expanded);
      return;
    }
    const link = event.target.closest('[data-article-toc-link]');
    if (!link) return;
    event.preventDefault();
    const target = this.tocItems.find(({ id }) => id === link.dataset.articleTocLink);
    if (!target) return;
    this.scrollingToHeading = target.id;
    this.setActiveTocItem(target.id);
    // Explicit coordinates avoid adding the theme's root scroll-padding a second time.
    window.scrollTo({
      top: Math.max(0, window.scrollY + target.element.getBoundingClientRect().top - this.headingOffset()),
      behavior: 'smooth'
    });
    this.scheduleHeadingScrollEnd();
  }

  headingOffset() {
    // Match the doctor-blogs template's activation line on both screen sizes.
    const header = document.querySelector('.header-container');
    const headerHeight = header?.getBoundingClientRect().height ||
      parseFloat(getComputedStyle(this).getPropertyValue('--header-height')) || 0;
    return Math.max(headerHeight, window.innerHeight * 0.13);
  }

  scheduleHeadingScrollEnd() {
    clearTimeout(this.headingScrollTimer);
    this.headingScrollTimer = setTimeout(() => { this.scrollingToHeading = null; }, 180);
  }

  observeHeadings() {
    const scheduleUpdate = () => {
      if (this.scrollingToHeading) {
        this.scheduleHeadingScrollEnd();
        return;
      }
      if (this.headingFrame) return;
      this.headingFrame = requestAnimationFrame(() => {
        this.headingFrame = null;
        this.updateActiveHeading();
      });
    };
    const options = { passive: true, signal: this.abortController.signal };
    window.addEventListener('scroll', scheduleUpdate, options);
    window.addEventListener('resize', scheduleUpdate, options);
    const cancelHeadingScroll = () => {
      this.scrollingToHeading = null;
      clearTimeout(this.headingScrollTimer);
    };
    window.addEventListener('wheel', cancelHeadingScroll, options);
    window.addEventListener('touchstart', cancelHeadingScroll, options);
    window.addEventListener('keydown', (event) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) cancelHeadingScroll();
    }, options);
    this.headingObserver = new ResizeObserver(scheduleUpdate);
    this.headingObserver.observe(this.content);
    this.updateActiveHeading();
  }

  updateActiveHeading() {
    // Share the anchor offset so clicking and scrolling activate at the same point.
    if (this.scrollingToHeading) return;
    const offset = this.headingOffset();
    let activeId = this.tocItems[0].id;
    for (const { id, element } of this.tocItems) {
      if (element.getBoundingClientRect().top > offset + 1) break;
      activeId = id;
    }
    this.setActiveTocItem(activeId);
  }

  setActiveTocItem(id) {
    this.querySelectorAll('[data-article-toc-link]').forEach((link) => {
      link.classList.toggle('is-active', link.dataset.articleTocLink === id);
    });
  }
}

if (!customElements.get('article-editorial')) customElements.define('article-editorial', ArticleEditorial);

class ArticleEditorialSignup {
  static SECTION_SELECTOR = '[data-article-editorial-signup-section]';
  static EMAIL_SELECTOR = '[data-article-editorial-signup-email]';
  static CHECKBOX_SELECTOR = '[data-article-editorial-signup-checkbox]';
  static SUBMIT_SELECTOR = '[data-article-editorial-signup-submit]';
  static ERROR_SELECTOR = '[data-article-editorial-signup-error]';
  static SUCCESS_SELECTOR = '[data-article-editorial-signup-success]';
  static CONTENT_SELECTOR = '[data-article-editorial-signup-content]';
  static EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  static SERVER_SUCCESS_STORAGE_PREFIX = 'articleEditorialSignupServerSuccess:';
  static PENDING_EMAIL_STORAGE_PREFIX = 'articleEditorialSignupPendingEmail:';

  constructor(section) {
    this.section = section;
    this.successResetTimer = null;
    this.abortController = new AbortController();
  }

  static isValidEmail(value) {
    return ArticleEditorialSignup.EMAIL_PATTERN.test(String(value || '').trim());
  }

  static parseCustomerFormResult(html) {
    const responseDocument = new DOMParser().parseFromString(html || '', 'text/html');
    const error = responseDocument.querySelector(
      '[data-article-editorial-signup-error]:not([hidden])'
    );

    return {
      errorMessage: error?.textContent?.trim() || '',
    };
  }

  static getErrorMessage(section, responseResult) {
    const fallbackMessage = section.querySelector(ArticleEditorialSignup.ERROR_SELECTOR)?.textContent?.trim() || 'Invalid email address';
    return responseResult.errorMessage || fallbackMessage;
  }

  getStorageKey(prefix) {
    const sectionId = this.section.closest('.shopify-section')?.id || 'section';
    return `${prefix}${location.pathname}:${sectionId}`;
  }

  getPendingEmail() {
    try {
      return sessionStorage.getItem(this.getStorageKey(ArticleEditorialSignup.PENDING_EMAIL_STORAGE_PREFIX)) || '';
    } catch {
      return '';
    }
  }

  setPendingEmail(email) {
    try {
      sessionStorage.setItem(this.getStorageKey(ArticleEditorialSignup.PENDING_EMAIL_STORAGE_PREFIX), email);
    } catch {
      // The immediate AJAX success state does not depend on storage.
    }
  }

  getServerSuccessStorageKey() {
    const email = this.section.querySelector(ArticleEditorialSignup.EMAIL_SELECTOR)?.value?.trim() || this.getPendingEmail();
    return `${this.getStorageKey(ArticleEditorialSignup.SERVER_SUCCESS_STORAGE_PREFIX)}:${email}`;
  }

  wasServerSuccessConsumed() {
    try {
      return sessionStorage.getItem(this.getServerSuccessStorageKey()) === 'true';
    } catch {
      return false;
    }
  }

  markServerSuccessConsumed() {
    try {
      sessionStorage.setItem(this.getServerSuccessStorageKey(), 'true');
    } catch {
      // The immediate server success state does not depend on storage.
    }
  }

  setEmailError(shouldShow) {
    const error = this.section.querySelector(ArticleEditorialSignup.ERROR_SELECTOR);
    const success = this.section.querySelector(ArticleEditorialSignup.SUCCESS_SELECTOR);
    const email = this.section.querySelector(ArticleEditorialSignup.EMAIL_SELECTOR);
    const field = email?.closest('.article-editorial__newsletter-field');

    if (error) error.hidden = !shouldShow;
    if (success && shouldShow) success.hidden = true;
    field?.classList.toggle('is-error', shouldShow);
    email?.setAttribute('aria-invalid', shouldShow ? 'true' : 'false');
  }

  setSuccessState(shouldShow) {
    const content = this.section.querySelector(ArticleEditorialSignup.CONTENT_SELECTOR);
    const success = this.section.querySelector(ArticleEditorialSignup.SUCCESS_SELECTOR);
    if (success) success.hidden = !shouldShow;
    content?.classList.toggle('is-success', shouldShow);
  }

  clearSuccessResetTimer() {
    window.clearTimeout(this.successResetTimer);
    this.successResetTimer = null;
  }

  resetToInitialState() {
    const email = this.section.querySelector(ArticleEditorialSignup.EMAIL_SELECTOR);
    const checkbox = this.section.querySelector(ArticleEditorialSignup.CHECKBOX_SELECTOR);

    this.clearSuccessResetTimer();
    this.setSuccessState(false);
    this.setEmailError(false);
    if (email) email.value = '';
    if (checkbox) checkbox.checked = false;
    this.section.querySelector(ArticleEditorialSignup.SUBMIT_SELECTOR)?.removeAttribute('aria-busy');
    this.updateSubmitState();
  }

  showSuccess() {
    this.clearSuccessResetTimer();
    this.setEmailError(false);
    this.setSuccessState(true);
    const delay = Number(this.section.dataset.articleEditorialSignupResetDelay) || 3;
    this.successResetTimer = window.setTimeout(() => this.resetToInitialState(), delay * 1000);
  }

  hideSuccess() {
    this.clearSuccessResetTimer();
    this.setSuccessState(false);
  }

  handleInitialServerSuccess() {
    const success = this.section.querySelector(ArticleEditorialSignup.SUCCESS_SELECTOR);
    if (success?.dataset.articleEditorialSignupServerSuccess !== 'true') return false;

    success.dataset.articleEditorialSignupServerSuccess = 'false';
    if (this.wasServerSuccessConsumed()) {
      this.resetToInitialState();
      return true;
    }

    this.markServerSuccessConsumed();
    this.showSuccess();
    return true;
  }

  showError(message) {
    const error = this.section.querySelector(ArticleEditorialSignup.ERROR_SELECTOR);
    if (error && message) error.textContent = message;
    this.setEmailError(true);
  }

  updateSubmitState(options = {}) {
    const email = this.section.querySelector(ArticleEditorialSignup.EMAIL_SELECTOR);
    const checkbox = this.section.querySelector(ArticleEditorialSignup.CHECKBOX_SELECTOR);
    const submit = this.section.querySelector(ArticleEditorialSignup.SUBMIT_SELECTOR);
    if (!email || !checkbox || !submit) return false;

    const emailHasValue = email.value.trim() !== '';
    const emailIsValid = ArticleEditorialSignup.isValidEmail(email.value);
    const canSubmit = emailIsValid && checkbox.checked;
    submit.disabled = !canSubmit;

    if (options.showEmailError) {
      this.setEmailError(emailHasValue && !emailIsValid);
    } else if (!emailHasValue || emailIsValid) {
      this.setEmailError(false);
    }
    return canSubmit;
  }

  refreshState() {
    this.section.querySelector(ArticleEditorialSignup.SUBMIT_SELECTOR)?.removeAttribute('aria-busy');
    if (this.handleInitialServerSuccess()) return;
    if (!this.section.querySelector(ArticleEditorialSignup.SUCCESS_SELECTOR)?.hidden) {
      this.showSuccess();
      return;
    }
    this.updateSubmitState();
  }

  async handleSubmit(event) {
    event?.preventDefault();
    const email = this.section.querySelector(ArticleEditorialSignup.EMAIL_SELECTOR);
    const submit = this.section.querySelector(ArticleEditorialSignup.SUBMIT_SELECTOR);
    const form = email?.closest('form');
    if (!form) return;

    if (!this.updateSubmitState({ showEmailError: true })) {
      email?.focus();
      return;
    }

    this.setPendingEmail(email?.value.trim() || '');
    submit?.setAttribute('aria-busy', 'true');
    if (submit) submit.disabled = true;
    this.setEmailError(false);
    this.hideSuccess();

    try {
      const response = await fetch(form.action, {
        method: form.method,
        body: new URLSearchParams(new FormData(form)),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (response.status < 400) {
        this.showSuccess();
        return;
      }
      const html = await response.text();
      const responseResult = ArticleEditorialSignup.parseCustomerFormResult(html);
      this.showError(ArticleEditorialSignup.getErrorMessage(this.section, responseResult));
    } catch (error) {
      console.error('Error:', error);
      this.showError();
    } finally {
      submit?.removeAttribute('aria-busy');
      this.updateSubmitState();
    }
  }

  bindEvents() {
    const { signal } = this.abortController;
    const email = this.section.querySelector(ArticleEditorialSignup.EMAIL_SELECTOR);
    const checkbox = this.section.querySelector(ArticleEditorialSignup.CHECKBOX_SELECTOR);
    const submit = this.section.querySelector(ArticleEditorialSignup.SUBMIT_SELECTOR);
    const form = email?.closest('form');
    email?.addEventListener('input', () => {
      this.hideSuccess();
      this.updateSubmitState();
    }, { signal });
    email?.addEventListener('blur', () => this.updateSubmitState({ showEmailError: true }), { signal });
    checkbox?.addEventListener('change', () => this.updateSubmitState(), { signal });
    checkbox?.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter') {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        checkbox.click();
      },
      { signal },
    );
    submit?.addEventListener('click', (event) => this.handleSubmit(event), { signal });
    form?.addEventListener('submit', (event) => this.handleSubmit(event), { signal });
  }

  init() {
    if (this.section.dataset.articleEditorialSignupInitialized === 'true') {
      this.refreshState();
      return;
    }
    this.bindEvents();
    this.refreshState();
    this.section.dataset.articleEditorialSignupInitialized = 'true';
    this.section._articleEditorialSignupInstance = this;
  }

  destroy() {
    this.clearSuccessResetTimer();
    this.abortController.abort();
    delete this.section._articleEditorialSignupInstance;
    delete this.section.dataset.articleEditorialSignupInitialized;
  }

  static getSections(root = document) {
    const sections = [];
    if (root.matches?.(ArticleEditorialSignup.SECTION_SELECTOR)) sections.push(root);
    root.querySelectorAll?.(ArticleEditorialSignup.SECTION_SELECTOR).forEach((section) => sections.push(section));
    return sections;
  }

  static initializeAll(root = document) {
    ArticleEditorialSignup.getSections(root).forEach((section) => new ArticleEditorialSignup(section).init());
  }

  static refreshAll(root = document) {
    ArticleEditorialSignup.getSections(root).forEach((section) => section._articleEditorialSignupInstance?.refreshState());
  }

  static destroyAllIn(root) {
    ArticleEditorialSignup.getSections(root).forEach((section) => section._articleEditorialSignupInstance?.destroy());
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ArticleEditorialSignup.initializeAll());
} else {
  ArticleEditorialSignup.initializeAll();
}

window.addEventListener('pageshow', () => ArticleEditorialSignup.refreshAll());
document.addEventListener('shopify:section:load', (event) => ArticleEditorialSignup.initializeAll(event.target));
document.addEventListener('shopify:section:unload', (event) => ArticleEditorialSignup.destroyAllIn(event.target));
